import React from 'react';
import { motion } from 'framer-motion';
import GlassCard from '../components/UI/GlassCard';
import CyberButton from '../components/UI/CyberButton';
import HUDElement from '../components/UI/HUDElement';
import NeonText from '../components/UI/NeonText';
import { useScrollAnimation, scrollVariants, staggerContainer } from '../hooks/useScrollAnimation';

const About = () => {
    const { ref: heroRef, controls: heroControls } = useScrollAnimation();

    const teamMembers = [
        {
            id: 1,
            name: 'Dr. Kaito Nakamura',
            role: 'Chief Technology Officer',
            specialization: 'IA & Machine Learning',
            experience: '15+ años',
            avatar: '/api/placeholder/100/100',
            skills: ['Neural Networks', 'Quantum Computing', 'System Architecture'],
            bio: 'Pionero en desarrollo de sistemas de inteligencia artificial y arquitecturas neurales.'
        },
        {
            id: 2,
            name: 'Sakura Tanaka',
            role: 'Lead Cybersecurity Expert',
            specialization: 'Security & Encryption',
            experience: '12+ años',
            avatar: '/api/placeholder/100/100',
            skills: ['Quantum Encryption', 'Pentesting', 'Security Protocols'],
            bio: 'Especialista en protección de sistemas críticos y desarrollo de protocolos de seguridad.'
        },
        {
            id: 3,
            name: 'Kenji Sato',
            role: 'XR Development Lead',
            specialization: 'Virtual & Augmented Reality',
            experience: '10+ años',
            avatar: '/api/placeholder/100/100',
            skills: ['VR Development', 'Haptic Systems', 'Spatial Computing'],
            bio: 'Creador de experiencias inmersivas y sistemas de realidad extendida.'
        },
        {
            id: 4,
            name: 'Aiko Chen',
            role: 'Blockchain Architect',
            specialization: 'Web3 & DeFi',
            experience: '8+ años',
            avatar: '/api/placeholder/100/100',
            skills: ['Smart Contracts', 'DApp Development', 'Tokenomics'],
            bio: 'Arquitecta de soluciones descentralizadas y economías digitales.'
        }
    ];

    const values = [
        {
            icon: '🔍',
            title: 'Innovación Constante',
            description: 'Siempre en la vanguardia tecnológica, explorando nuevas fronteras digitales.'
        },
        {
            icon: '🛡️',
            title: 'Seguridad Integral',
            description: 'Priorizamos la protección de datos y sistemas en cada implementación.'
        },
        {
            icon: '⚡',
            title: 'Excelencia Técnica',
            description: 'Comprometidos con la más alta calidad en desarrollo y ejecución.'
        },
        {
            icon: '🌐',
            title: 'Impacto Global',
            description: 'Desarrollamos soluciones que transforman industrias y mejoran vidas.'
        }
    ];

    const timeline = [
        {
            year: '2018',
            event: 'Fundación de TECH_ANIME',
            description: 'Establecimiento del laboratorio de innovación tecnológica'
        },
        {
            year: '2019',
            event: 'Primer Sistema de IA Propio',
            description: 'Desarrollo y patentamiento de arquitectura neural única'
        },
        {
            year: '2020',
            event: 'Expansión Global',
            description: 'Apertura de sedes en Tokio, Silicon Valley y Berlín'
        },
        {
            year: '2021',
            event: 'Premio a la Innovación',
            description: 'Reconocimiento por avances en computación cuántica'
        },
        {
            year: '2022',
            event: 'Lanzamiento Plataforma Neuro-Link',
            description: 'Primera interfaz comercial cerebro-computadora'
        },
        {
            year: '2023',
            event: 'Alianza Estratégica',
            description: 'Colaboración con líderes mundiales en tecnología'
        }
    ];

    return (
        <div className="about-page">
            {/* Hero Section */}
            <motion.section
                className="page-hero"
                ref={heroRef}
                initial="hidden"
                animate={heroControls}
                variants={scrollVariants}
            >
                <div className="container">
                    <div className="hero-content">
                        <NeonText variant="cyan" size="large" animated={true}>
                            // SISTEMA_TECH_ANIME
                        </NeonText>
                        <h1 className="hero-title">
                            Arquitectos del
                            <span className="accent"> Futuro Digital</span>
                        </h1>
                        <p className="hero-description">
                            En TECH_ANIME, fusionamos el arte del código con la ciencia
                            de vanguardia. Somos un colectivo de visionarios tecnológicos
                            dedicados a construir soluciones que trascienden los límites
                            de lo posible.
                        </p>

                        <div className="hero-stats">
                            <HUDElement label="Años de Innovación" value={6} unit="+" />
                            <HUDElement label="Proyectos Exitosos" value={150} unit="+" />
                            <HUDElement label="Países Activos" value={25} unit="+" />
                            <HUDElement label="Equipo de Expertos" value={50} unit="+" />
                        </div>
                    </div>
                </div>
            </motion.section>

            {/* Mission & Vision */}
            <section className="section mission-section">
                <div className="container">
                    <div className="mission-grid">
                        <motion.div
                            initial={{ opacity: 0, x: -50 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6 }}
                        >
                            <GlassCard>
                                <NeonText variant="cyan" size="medium">
                                    // MISION_SISTEMA
                                </NeonText>
                                <h3>Nuestra Misión</h3>
                                <p>
                                    Acelerar la evolución tecnológica humana mediante el desarrollo
                                    de sistemas inteligentes que amplifiquen el potencial creativo
                                    y resolutivo de la sociedad.
                                </p>
                                <ul className="mission-list">
                                    <li>Desarrollar IA ética y accesible</li>
                                    <li>Proteger la privacidad digital</li>
                                    <li>Democratizar tecnología avanzada</li>
                                    <li>Fomentar innovación responsable</li>
                                </ul>
                            </GlassCard>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, x: 50 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6, delay: 0.2 }}
                        >
                            <GlassCard>
                                <NeonText variant="magenta" size="medium">
                                    // VISION_FUTURO
                                </NeonText>
                                <h3>Nuestra Visión</h3>
                                <p>
                                    Visualizamos un futuro donde la tecnología sirva como extensión
                                    armónica de las capacidades humanas, creando un ecosistema
                                    digital simbiótico y sostenible.
                                </p>
                                <ul className="vision-list">
                                    <li>Interfaces neurales no invasivas</li>
                                    <li>Computación cuántica accesible</li>
                                    <li>Realidades extendidas integradas</li>
                                    <li>Inteligencia artificial colaborativa</li>
                                </ul>
                            </GlassCard>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* Values Section */}
            <section className="section values-section">
                <div className="container">
                    <motion.div
                        className="section-header"
                        initial={{ opacity: 0, y: 50 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                    >
                        <NeonText variant="magenta" size="large">
                            // PRINCIPIOS_OPERATIVOS
                        </NeonText>
                        <h2>Nuestros Valores Fundamentales</h2>
                        <p>Los pilares que guían cada desarrollo y decisión</p>
                    </motion.div>

                    <motion.div
                        className="values-grid"
                        variants={staggerContainer}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                    >
                        {values.map((value, index) => (
                            <motion.div key={value.title} variants={scrollVariants}>
                                <GlassCard className="value-card">
                                    <div className="value-icon">{value.icon}</div>
                                    <h3>{value.title}</h3>
                                    <p>{value.description}</p>
                                </GlassCard>
                            </motion.div>
                        ))}
                    </motion.div>
                </div>
            </section>

            {/* Team Section */}
            <section className="section team-section">
                <div className="container">
                    <motion.div
                        className="section-header"
                        initial={{ opacity: 0, y: 50 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                    >
                        <NeonText variant="cyan" size="large">
                            // EQUIPO_TECNICO
                        </NeonText>
                        <h2>Mentes Maestras del Sistema</h2>
                        <p>El equipo de especialistas que impulsa nuestra innovación</p>
                    </motion.div>

                    <div className="team-grid">
                        {teamMembers.map((member, index) => (
                            <motion.div
                                key={member.id}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: index * 0.1 }}
                            >
                                <GlassCard className="team-card">
                                    <div className="member-avatar">
                                        <div className="avatar-placeholder">
                                            {member.name.split(' ').map(n => n[0]).join('')}
                                        </div>
                                    </div>
                                    <div className="member-info">
                                        <h3>{member.name}</h3>
                                        <p className="member-role">{member.role}</p>
                                        <p className="member-specialization">{member.specialization}</p>
                                        <div className="member-experience">
                                            <span>{member.experience} experiencia</span>
                                        </div>
                                    </div>
                                    <p className="member-bio">{member.bio}</p>
                                    <div className="member-skills">
                                        {member.skills.map((skill, skillIndex) => (
                                            <span key={skillIndex} className="skill-tag">
                        {skill}
                      </span>
                                        ))}
                                    </div>
                                </GlassCard>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Timeline Section */}
            <section className="section timeline-section">
                <div className="container">
                    <motion.div
                        className="section-header"
                        initial={{ opacity: 0, y: 50 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                    >
                        <NeonText variant="magenta" size="large">
                            // LINEA_TEMPORAL
                        </NeonText>
                        <h2>Nuestra Evolución</h2>
                        <p>Momentos clave en nuestro viaje de innovación</p>
                    </motion.div>

                    <div className="timeline">
                        {timeline.map((item, index) => (
                            <motion.div
                                key={item.year}
                                className={`timeline-item ${index % 2 === 0 ? 'left' : 'right'}`}
                                initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.6, delay: index * 0.1 }}
                            >
                                <GlassCard className="timeline-card">
                                    <div className="timeline-year">{item.year}</div>
                                    <h3 className="timeline-event">{item.event}</h3>
                                    <p className="timeline-description">{item.description}</p>
                                </GlassCard>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>
        </div>
    );
};

export default About;